/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  Shape,
  CircleShape,
  LineShape,
  PolygonShape,
  CurveShape,
  ChamferShape,
  Point3,
} from '@/library/step-generator/model/types'

import * as CAD from '@/library/occt/cad'

function scaleP(p: Point3, zOffset: number, s: number): Point3 {
  return {
    x: p.x * s,
    y: p.y * s,
    z: (p.z * s) + zOffset,
  }
}

// Circle center.z = E，圆柱范围 [E, E+H] ✓ 不需要调整
function buildCircleShape(oc: any, shape: CircleShape, zOffset: number, s: number) {
  let solid = CAD.makeCylinderZ(oc, shape.radius * s, shape.height * s)
  const base = scaleP(shape.center, zOffset, s)
  solid = CAD.translate(oc, solid, base.x, base.y, base.z)
  return solid
}

// Line start.z = E + H/2（Path.buildPayload 加了 height/2）
// 使用 makeLineBox 正确旋转 box 到 start→end 方向
// box 从 start.z-h/2 到 start.z+h/2 = [E, E+H] ✓
function buildLineShape(oc: any, shape: LineShape, zOffset: number, s: number) {
  const start = scaleP(shape.start, zOffset, s)
  const end = scaleP(shape.end, zOffset, s)
  const w = shape.width * s
  const h = shape.height * s

  // makeLineBox internally centers the profile around start and end
  return CAD.makeLineBox(
    oc,
    { x: start.x, y: start.y, z: start.z },
    { x: end.x,   y: end.y,   z: end.z },
    w,
    h
  )
}

// Polygon points.z = E，direction.z = H，范围 [E, E+H] ✓ 不需要调整
function buildPolygonShape(oc: any, shape: PolygonShape, zOffset: number, s: number) {
  const pts = shape.points.map((p: Point3) => scaleP(p, zOffset, s))
  const scaledDir = { x: shape.direction.x * s, y: shape.direction.y * s, z: shape.direction.z * s }
  return CAD.makePolygonPrism(oc, pts, scaledDir)
}

function buildChamferShape(oc: any, shape: ChamferShape, zOffset: number, s: number) {
  let solid = CAD.makeConeZ(oc, shape.radius * s, shape.radius_top * s, shape.height * s)
  const base = scaleP(shape.center, zOffset, s)
  solid = CAD.translate(oc, solid, base.x, base.y, base.z)
  return solid
}

// Curve start.z = E + H/2（Path.buildPayload 加了 height/2）
// makeArcPipeRect 从 start.z 向上延伸 H → [E+H/2, E+3H/2]
// 构建后平移 -H/2 → [E, E+H] ✓
function isBridgeCurve(shape: CurveShape): boolean {
  if (!shape.start || !shape.center || !shape.end) return false;
  const zDiffStartEnd = Math.abs(shape.start.z - shape.end.z)
  const zDiffStartCenter = Math.abs(shape.start.z - shape.center.z)
  const zDiffEndCenter = Math.abs(shape.end.z - shape.center.z)
  const maxZDiff = Math.max(zDiffStartEnd, zDiffStartCenter, zDiffEndCenter)
  return maxZDiff > 1e-5
}

function buildCurveShape(oc: any, shape: CurveShape, zOffset: number, s: number) {
  const start = scaleP(shape.start, zOffset, s)
  const end = scaleP(shape.end, zOffset, s)
  const center = scaleP(shape.center, zOffset, s)
  
  const isBridge = isBridgeCurve(shape)
  // For bridge curves, the JSON payload has width and height swapped relative to flat curves
  const w = isBridge ? shape.height * s : shape.width * s
  const h = isBridge ? shape.width * s : shape.height * s
  console.log(`[STEP] Curve isBridge=${isBridge}, JSON w=${shape.width}, h=${shape.height} -> Output w=${w/s}, h=${h/s}`)

  // 直接构建居中的弧线实体（保证 MakeRevol 成功且不破坏 bridge 几何）
  const solid = CAD.makeArcPipeRect(
    oc,
    start,
    end,
    center,
    w,
    h,
    shape.tangent
  )

  return solid
}

function buildHillShape(oc: any, shape: any, zOffset: number, s: number) {
  const p0 = scaleP(shape.points[0], zOffset, s);
  const p1 = scaleP(shape.points[1], zOffset, s);
  const p2 = scaleP(shape.points[2], zOffset, s);
  const p3 = scaleP(shape.points[3], zOffset, s);

  const dx = p3.x - p0.x;
  const dy = p3.y - p0.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) throw new Error("Hill has zero length");

  const lx = -dy / len;
  const ly = dx / len;

  const w = shape.width * s;
  const hw = w / 2;

  const hh = shape.height * s * 0.5;

  const vp0 = { x: p0.x - lx * hw, y: p0.y - ly * hw, z: p0.z + hh };
  const vp1 = { x: p1.x - lx * hw, y: p1.y - ly * hw, z: p1.z + hh };
  const vp2 = { x: p2.x - lx * hw, y: p2.y - ly * hw, z: p2.z + hh };
  const vp3 = { x: p3.x - lx * hw, y: p3.y - ly * hw, z: p3.z + hh };

  const floorZ_val = Math.min(p0.z, p3.z) - hh;
  const vpFloor3 = { x: vp3.x, y: vp3.y, z: floorZ_val };
  const vpFloor0 = { x: vp0.x, y: vp0.y, z: floorZ_val };

  const pts = [vp0, vp1, vp2, vp3, vpFloor3, vpFloor0];
  const dirVec = { x: lx * w, y: ly * w, z: 0 };

  return CAD.makePolygonPrism(oc, pts, dirVec);
}

export function buildShapeToOcct(oc: any, shape: Shape, zOffset = 0, scale = 1) {
  switch ((shape as any).type) {
    case 'Circle':
      return buildCircleShape(oc, shape as any, zOffset, scale)

    case 'Line':
      return buildLineShape(oc, shape as any, zOffset, scale)

    case 'Polygon':
      return buildPolygonShape(oc, shape as any, zOffset, scale)

    case 'Curve':
      return buildCurveShape(oc, shape as any, zOffset, scale)

    case 'Chamfer':
      return buildChamferShape(oc, shape as any, zOffset, scale)

    case 'Hill':
      return buildHillShape(oc, shape, zOffset, scale)

    default:
      throw new Error(`Unsupported shape type: ${(shape as any)?.type}`)
  }
}